import { InputRule, Node, PasteRule } from '@tiptap/core';

export interface TokenOptions {
  tokenRegex: RegExp;
  tokenClass: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    token: {
      setToken: (attributes: {
        class: string;
        content: string;
        'data-type': string;
      }) => ReturnType;
    };
  }
}

export const Token = Node.create<TokenOptions>({
  name: 'token',

  group: 'inline',
  inline: true,
  draggable: true,
  selectable: true,
  atom: true,

  addOptions() {
    return {
      tokenRegex: /(\[token:(.*?)\])/g,
      tokenClass: 'token',
    };
  },

  addAttributes() {
    return {
      content: {
        default: '',
      },
      class: {
        default: this.options.tokenClass,
      },
      'data-uuid': {
        default: '',
      },
      'data-type': {
        default: 'node',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.token',
        getAttrs: (node) => {
          if (typeof node === 'string') return false;
          const element = node as HTMLElement;

          const text = element.textContent || '';
          const match = text.match(/\[token:([^\]]+)\]/);
          const content = match ? match[1] : text;

          return {
            content: content,
            class: element.className,
            'data-type': element.getAttribute('data-type') || 'node',
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const type = node.attrs['data-type'] || 'node';
    const typeClass = `token-type-${type}`;

    return [
      'span',
      {
        ...HTMLAttributes,
        class: `${this.options.tokenClass} ${typeClass}`,
        'data-token': 'true',
        'data-token-content': node.attrs.content,
      },
      ['span', { class: 'token-icon' }, 0],
      ['span', { class: 'token-content' }, node.attrs.content],
      [
        'button',
        {
          class: 'token-delete',
          type: 'button',
          'data-token-delete': 'true',
          onclick: "this.closest('[data-token]').remove()",
        },
        '×',
      ],
    ];
  },

  addCommands() {
    return {
      setToken:
        (attributes) =>
        ({ commands }) => {
          return commands.setNode(this.name, attributes);
        },
    };
  },

  addInputRules() {
    return [
      new InputRule({
        find: /(?:^|\s)(\[token:([^\]]+)\])$/,
        handler: ({ state, range, match }) => {
          const { from, to } = range;
          const content = match[2];
          const { tr } = state;

          tr.insertText(' ', to);
          tr.insertText(' ', from);

          tr.replaceWith(
            from + 1,
            to + 1,
            state.schema.node('token', {
              content: content,
              class: this.options.tokenClass,
              'data-type': 'node',
            })
          );
        },
      }),
    ];
  },

  addPasteRules() {
    return [
      new PasteRule({
        find: this.options.tokenRegex,
        handler: ({ state, range, match }) => {
          const { from, to } = range;
          const content = match[2];
          const { tr } = state;

          tr.insertText(' ', to);
          tr.insertText(' ', from);

          tr.replaceWith(
            from + 1,
            to + 1,
            state.schema.node('token', {
              content: content,
              class: this.options.tokenClass,
              'data-type': 'node',
            })
          );
        },
      }),
    ];
  },
});
